import { css } from '@emotion/core';
import {
  pxToRem,
  minimumWidth,
  styled,
  Theme,
  Link,
  textStyle,
  Heading,
  Icon,
} from '@feast-it/ui/light';

const styleArea = (name: string, theme: Theme, isDark?: boolean) => {
  switch (name) {
    case 'payments':
      return `
        border-top: 1px solid ${theme.colors.togatherBlue};
        ${minimumWidth(theme.breakpoints.large)} {
          border-top: none
        }
      `;
    case 'copyright':
      return `
        border-top: 1px solid ${
          isDark ? theme.colors.mediumDarkGrey : theme.colors.togatherBlue
        };
      `;
    case 'reviews':
      return `
        display: flex;
        justify-content: center;
        align-items: flex-start;
        height: 100%;

        ${minimumWidth(theme.breakpoints.large)} {
          justify-content: flex-start;
        }
      `;
    default:
      return null;
  }
};

export const Container = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: grid;
    margin-bottom: ${theme.spacing[14]};
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'newsletter newsletter'
      'services about'
      'info reviews'
      'payments payments'
      'copyright copyright';
    text-align: center;

    ${minimumWidth(theme.breakpoints.small)} {
      margin-bottom: ${theme.spacing[16]};
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-template-areas:
        'newsletter newsletter newsletter newsletter'
        'services about info reviews'
        'payments payments payments payments'
        'copyright copyright copyright copyright';
    }

    ${minimumWidth(theme.breakpoints.large)} {
      margin-bottom: ${theme.spacing[10]};
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      grid-template-areas:
        'reviews services about info payments'
        'newsletter newsletter newsletter newsletter newsletter'
        'copyright copyright copyright copyright copyright';
      text-align: left;
    }
  `
);

export const Area = styled.div<{ name: string; as?: string; isDark?: boolean }>(
  ({ theme, name, isDark }) => css`
    padding: ${theme.spacing[8]} 0;
    grid-area: ${name};
    ${styleArea(name, theme, isDark)};
  `
);

export const InternalLink = styled.p(
  ({ theme, isDark }) => css`
    ${textStyle(theme, 'bodySmall', isDark ? 'white' : 'darkGrey')};

    &:hover {
      color: ${isDark ? theme.colors.lightGrey : theme.colors.mediumBlue};
      cursor: pointer;
    }
  `
);

export const PaymentGrid = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-row-gap: ${theme.spacing[4]};
    grid-template-columns: 1fr 1fr 1fr 1fr;
    max-width: ${pxToRem(320)};
    margin: 0 auto;
    opacity: 0.7;

    > * {
      margin: 0 auto;
    }

    ${minimumWidth(theme.breakpoints.large)} {
      margin: 0;
      opacity: initial;
      grid-template-columns: 1fr 1fr;
      max-width: ${pxToRem(200)};

      > * {
        margin: 0;
      }
    }
  `
);

export const StyledIconSocial = styled(Icon)(
  ({ theme }) => css`
    ${minimumWidth(theme.breakpoints.large)} {
      > svg {
        width: ${theme.spacing[6]};
        height: ${theme.spacing[6]};
      }
    }
  `
);

export const SocialLinks = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const LogoWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: center;

    ${minimumWidth(theme.breakpoints.large)} {
      justify-content: flex-start;
    }
  `
);

export const SocialLinkItem = styled.li(
  ({ theme }) => css`
    aspect-ratio: 1/1;
    height: ${theme.spacing[6]};
    width: ${theme.spacing[6]};

    & + & {
      margin-left: ${theme.spacing[4]};
    }
  `
);

export const StyledLink = styled(Link)(
  ({ theme, isDark }) => css`
    ${textStyle(theme, 'bodyMedium', isDark ? 'white' : 'darkGrey')};
    margin-bottom: ${theme.spacing[2]};
  `
);

export const StyledList = styled.ul(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing[2]};
  `
);

export const StyledListItem = styled.li(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing[2]};
  `
);

export const StyledHeading = styled(Heading)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing[2]};
  `
);
